import { FilterList } from 'iconoir-react';
import { useTranslation } from 'react-i18next';
import { cn } from 'lib/utils';
import { useTheme } from 'components/core/theme-provider';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import type { TeamsMember } from '../teams-mock-data';
import { teamsOutlineCardButtonClassName } from './teams-styles';

/** A plan the organization can give, from the seat pools (same list as the Plan column). */
export type TeamsPlanFilterOption = { code: string; name: string };

export type TeamsPeopleFilters = {
  plan: string;
  request: string;
  creditAccess: string;
};

export const TEAMS_PEOPLE_FILTERS_DEFAULT: TeamsPeopleFilters = {
  plan: 'any',
  request: 'any',
  creditAccess: 'any',
};

type FilterField = {
  key: keyof TeamsPeopleFilters;
  labelKey: string;
  defaultLabel: string;
  /** Options without a labelKey are plan names, shown as they are. */
  options: Array<{ value: string; labelKey?: string; defaultLabel: string }>;
};

function planField(planOptions: TeamsPlanFilterOption[]): FilterField {
  return {
    key: 'plan',
    labelKey: 'TEAMS_FILTER_PLAN',
    defaultLabel: 'Plan',
    options: [
      { value: 'any', labelKey: 'TEAMS_FILTER_ANY_PLAN', defaultLabel: 'Any plan' },
      ...planOptions.map((plan) => ({ value: plan.code, defaultLabel: plan.name })),
      { value: 'none', labelKey: 'TEAMS_FILTER_NO_PLAN', defaultLabel: 'No plan' },
    ],
  };
}

const OTHER_FILTER_FIELDS: FilterField[] = [
  {
    key: 'request',
    labelKey: 'TEAMS_FILTER_REQUEST',
    defaultLabel: 'Request',
    options: [
      { value: 'any', labelKey: 'TEAMS_FILTER_ANYONE', defaultLabel: 'Anyone' },
      {
        value: 'upgrade',
        labelKey: 'TEAMS_FILTER_REQUEST_UPGRADE',
        defaultLabel: 'Upgrade request',
      },
      {
        value: 'credit',
        labelKey: 'TEAMS_FILTER_REQUEST_CREDIT',
        defaultLabel: 'Credit request',
      },
      { value: 'none', labelKey: 'TEAMS_FILTER_REQUEST_NONE', defaultLabel: 'No request' },
    ],
  },
  {
    key: 'creditAccess',
    labelKey: 'TEAMS_FILTER_CREDIT_ACCESS',
    defaultLabel: 'Credit access',
    options: [
      { value: 'any', labelKey: 'TEAMS_FILTER_ANYONE', defaultLabel: 'Anyone' },
      { value: 'yes', labelKey: 'TEAMS_FILTER_CREDIT_ON', defaultLabel: 'Has access' },
      { value: 'no', labelKey: 'TEAMS_FILTER_CREDIT_OFF', defaultLabel: 'No access' },
    ],
  },
];

export function teamsPeopleFiltersAreActive(filters: TeamsPeopleFilters) {
  return (Object.keys(TEAMS_PEOPLE_FILTERS_DEFAULT) as Array<keyof TeamsPeopleFilters>).some(
    (key) => filters[key] !== TEAMS_PEOPLE_FILTERS_DEFAULT[key]
  );
}

export function applyTeamsPeopleLocalFilters(
  members: TeamsMember[],
  filters: TeamsPeopleFilters,
  planCodeOf: (member: TeamsMember) => string | null,
  creditAccessById: Record<string, boolean>
) {
  return members.filter((member) => {
    const planCode = planCodeOf(member);
    if (filters.plan === 'none' && planCode != null) return false;
    if (filters.plan !== 'any' && filters.plan !== 'none' && planCode !== filters.plan) return false;

    if (filters.request === 'none' && member.request != null) return false;
    if (filters.request === 'upgrade' && member.request !== 'upgrade') return false;
    if (filters.request === 'credit' && member.request !== 'credit') return false;

    const creditAccess = creditAccessById[member.id] ?? member.creditAccess;
    if (filters.creditAccess === 'yes' && !creditAccess) return false;
    if (filters.creditAccess === 'no' && creditAccess) return false;

    return true;
  });
}

type TeamsPeopleFilterProps = {
  value: TeamsPeopleFilters;
  onChange: (next: TeamsPeopleFilters) => void;
  planOptions: TeamsPlanFilterOption[];
  className?: string;
};

export function TeamsPeopleFilter({ value, onChange, planOptions, className }: TeamsPeopleFilterProps) {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const labelColor = theme === 'dark' ? 'text-grey-400' : 'text-grey-600';
  const valueColor = theme === 'dark' ? 'text-true-white' : 'text-grey-900';
  const clearColor = theme === 'dark' ? 'text-grey-400' : 'text-grey-400';
  const borderColor = theme === 'dark' ? 'border-border' : 'border-grey-100';
  const isActive = teamsPeopleFiltersAreActive(value);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(teamsOutlineCardButtonClassName, valueColor, borderColor, className)}
          aria-pressed={isActive}
        >
          <FilterList width={16} height={16} strokeWidth={1.5} className="shrink-0" />
          {t('TEAMS_FILTER', { defaultValue: 'Filter' })}
        </button>
      </PopoverTrigger>
      {/* Figma 10631:7457 — People filter popover */}
      <PopoverContent
        align="start"
        sideOffset={8}
        className={cn(
          'w-72 rounded-xl border border-grey-100 bg-true-white p-4 shadow-choice-card',
          'dark:border-border dark:bg-card'
        )}
      >
        <div className="flex flex-col gap-3">
          {[planField(planOptions), ...OTHER_FILTER_FIELDS].map((field) => (
            <div key={field.key} className="flex min-w-0 flex-col gap-1.5">
              <label
                htmlFor={`teams-people-filter-${field.key}`}
                className={cn('text-xs font-medium leading-heading', labelColor)}
              >
                {t(field.labelKey, { defaultValue: field.defaultLabel })}
              </label>
              <Select
                value={value[field.key]}
                onValueChange={(next) => onChange({ ...value, [field.key]: next })}
              >
                <SelectTrigger
                  id={`teams-people-filter-${field.key}`}
                  className={cn(
                    'h-9 w-full rounded-card border bg-true-white px-2.5 text-sm font-normal shadow-none dark:bg-card',
                    'focus:ring-0 focus-visible:ring-0 [&>span]:line-clamp-1',
                    borderColor,
                    valueColor
                  )}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-card border-grey-100 dark:border-border">
                  {field.options.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.labelKey
                        ? t(option.labelKey, { defaultValue: option.defaultLabel })
                        : option.defaultLabel}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ))}

          <button
            type="button"
            disabled={!isActive}
            onClick={() => onChange(TEAMS_PEOPLE_FILTERS_DEFAULT)}
            className={cn(
              'flex h-9 w-full items-center justify-center rounded-card text-sm font-medium leading-body',
              'disabled:cursor-not-allowed disabled:opacity-50',
              clearColor,
              'hover:bg-grey-50 dark:hover:bg-muted'
            )}
          >
            {t('TEAMS_CLEAR_FILTERS', { defaultValue: 'Clear filters' })}
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
